import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Leaf, Award, Truck, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const seedAndFetch = async () => {
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/products/featured`);
        setFeaturedProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };
    seedAndFetch();
  }, []);

  const features = [
    {
      icon: <Leaf className="w-6 h-6" />,
      title: "100% Cotton",
      description: "Premium bio-washed fabric",
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "25 Years",
      description: "Manufacturing excellence",
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Fast Delivery",
      description: "All over India",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Quality Assured",
      description: "Comfort guaranteed",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1600264191365-3c0de61f4718?w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-1 bg-accent text-primary font-medium text-sm rounded-full mb-6">
              Since 1999 - Tirupur, India
            </span>
            <h1 className="font-heading text-5xl md:text-7xl text-white leading-tight mb-6" data-testid="hero-title">
              Feel the
              <br />
              <span className="text-accent">Pure Cotton</span>
              <br />
              Comfort
            </h1>
            <p className="text-white/90 text-lg md:text-xl mb-8 leading-relaxed">
              25 years of manufacturing excellence. 100% cotton, bio-washed fabric
              that's safe, comfortable, and makes you feel happy.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                className="bg-accent text-primary hover:bg-accent/90 rounded-full font-semibold text-base"
                data-testid="shop-mens-btn"
              >
                <Link to="/products/mens">
                  Shop Men's (U & Mi) <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-primary rounded-full font-semibold text-base"
                data-testid="shop-boys-btn"
              >
                <Link to="/products/boys">Shop Boys (KiTees)</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-4"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center text-primary mb-3">
                  {feature.icon}
                </div>
                <h3 className="font-heading font-bold text-lg mb-1">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Sections */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {/* Men's Section - U & Mi */}
            <Link
              to="/products/mens"
              className="md:col-span-7 relative group overflow-hidden rounded-lg aspect-[4/3] md:aspect-auto md:min-h-[400px]"
              data-testid="mens-brand-section"
            >
              <img
                src="https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=800"
                alt="U & Mi - Men's T-Shirts"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-mens/90 via-brand-mens/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <span className="text-white/80 text-sm font-medium tracking-wider uppercase">
                  For Men
                </span>
                <h2 className="font-heading text-4xl md:text-5xl text-white font-bold mt-2">
                  U & Mi
                </h2>
                <p className="text-white/80 mt-2 max-w-sm">
                  Premium cotton t-shirts for the modern gentleman
                </p>
                <Button
                  variant="outline"
                  className="mt-4 border-white text-white hover:bg-white hover:text-brand-mens rounded-full"
                >
                  Explore Collection <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Link>

            {/* Boys Section - KiTees */}
            <Link
              to="/products/boys"
              className="md:col-span-5 relative group overflow-hidden rounded-lg aspect-[4/3] md:aspect-auto md:min-h-[400px]"
              data-testid="boys-brand-section"
            >
              <img
                src="https://images.unsplash.com/photo-1595404295889-519c30990c36?w=800"
                alt="KiTees - Boys T-Shirts"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-boys/90 via-brand-boys/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <span className="text-white/80 text-sm font-medium tracking-wider uppercase">
                  For Boys
                </span>
                <h2 className="font-heading text-4xl md:text-5xl text-white font-bold mt-2">
                  KiTees
                </h2>
                <p className="text-white/80 mt-2">Fun, colorful & comfortable</p>
                <Button
                  variant="outline"
                  className="mt-4 border-white text-white hover:bg-white hover:text-brand-boys rounded-full"
                >
                  Shop Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              Featured Products
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Our best-selling t-shirts, loved by thousands of customers
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-gray-200 rounded-lg mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12" data-testid="featured-products-grid">
              {featuredProducts.slice(0, 6).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Button
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 rounded-full"
              data-testid="view-all-products-btn"
            >
              <Link to="/products">
                View All Products <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24 bg-cotton-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-primary font-medium tracking-wider uppercase text-sm">
                Our Story
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mt-2 mb-6">
                25 Years of Cotton Excellence
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Since 1999, OGAS has been at the forefront of quality t-shirt
                manufacturing in Tirupur, Tamil Nadu - India's textile capital. Our
                own manufacturing unit, combined with experienced staff and workers,
                ensures every piece meets our high standards.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                We use 100% cotton with bio-washed fabric that's not just comfortable
                but also safe for your skin. Every t-shirt we create is a testament
                to our commitment to quality and customer satisfaction.
              </p>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white rounded-full"
                data-testid="learn-more-btn"
              >
                <Link to="/about">Learn More About Us</Link>
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1741275269921-3c562cdb9176?w=600"
                alt="OGAS Manufacturing"
                className="rounded-lg shadow-hover w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-white p-6 rounded-lg shadow-lg">
                <div className="font-heading text-4xl font-bold">25+</div>
                <div className="text-white/80">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              What Customers Say
            </h2>
            <p className="text-muted-foreground text-lg">
              Trusted by thousands of happy customers across India
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Rajesh Kumar",
                location: "Chennai",
                text: "The quality of cotton is amazing! I've been buying from OGAS for 5 years now. Best t-shirts for Indian weather.",
                rating: 5,
              },
              {
                name: "Priya Sharma",
                location: "Bangalore",
                text: "My kids love KiTees! The colors stay vibrant even after multiple washes. Will definitely buy again.",
                rating: 5,
              },
              {
                name: "Amit Patel",
                location: "Mumbai",
                text: "U & Mi t-shirts are my go-to for office casual wear. Comfortable and looks great. Highly recommended!",
                rating: 5,
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-cotton-100 p-6 rounded-lg"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-accent"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-foreground mb-4">"{testimonial.text}"</p>
                <div>
                  <div className="font-medium">{testimonial.name}</div>
                  <div className="text-muted-foreground text-sm">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white font-bold mb-4">
            Ready to Experience Pure Cotton Comfort?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers. Shop now and feel the OGAS difference.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-accent text-primary hover:bg-accent/90 rounded-full font-semibold"
            data-testid="cta-shop-now-btn"
          >
            <Link to="/products">
              Shop Now <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
